'use client';

import {
  ListBox as HeroListBox,
  Select as HeroSelect,
  Description as HeroDescription,
} from '@heroui/react';
import { Key, ReactNode } from 'react';

import { FieldError } from './FieldError';
import { Label } from './Label';

export interface SelectOption {
  id: string | number;
  label: string;
  description?: string;
  textValue?: string;
}

export interface SelectProps {
  label?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  items: SelectOption[];
  placeholder?: string;
  description?: string | ReactNode;
  errorMessage?: string | ReactNode;
  isInvalid?: boolean;
  isRequired?: boolean;
  isDisabled?: boolean;
  variant?: 'primary' | 'secondary';
  selectionMode?: 'single' | 'multiple';
  value?: Key | null | Key[];
  onChange?: (value: Key | null | Key[]) => void;
  name?: string;
}

export const Select = ({
  label,
  items,
  placeholder,
  description,
  errorMessage,
  isInvalid,
  isRequired,
  isDisabled,
  variant = 'primary',
  selectionMode = 'single',
  value,
  onChange,
  name,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
}: SelectProps) => {
  const effectiveAriaLabel =
    ariaLabel ?? (!label && !ariaLabelledby ? placeholder : undefined);

  return (
    <HeroSelect
      name={name}
      isDisabled={isDisabled}
      isRequired={isRequired}
      isInvalid={isInvalid || !!errorMessage}
      variant={variant}
      placeholder={placeholder}
      selectionMode={selectionMode}
      onChange={onChange as any}
      value={value as any}
      className="w-full flex flex-col gap-1"
      aria-label={effectiveAriaLabel}
      aria-labelledby={ariaLabelledby}
    >
      {label && <Label>{label}</Label>}
      <HeroSelect.Trigger className="bg-content2 border-divider">
        <HeroSelect.Value />
        <HeroSelect.Indicator />
      </HeroSelect.Trigger>
      {description && <HeroDescription>{description}</HeroDescription>}
      <HeroSelect.Popover>
        <HeroListBox items={items} selectionMode={selectionMode}>
          {(item: SelectOption) => (
            <HeroListBox.Item
              key={item.id}
              id={item.id}
              textValue={item.textValue || item.label}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{item.label}</span>
                  {item.description && (
                    <span className="text-tiny text-default-400">
                      {item.description}
                    </span>
                  )}
                </div>
                <HeroListBox.ItemIndicator />
              </div>
            </HeroListBox.Item>
          )}
        </HeroListBox>
      </HeroSelect.Popover>
      {errorMessage && <FieldError>{errorMessage}</FieldError>}
    </HeroSelect>
  );
};
